import React, { useEffect, useRef, useState } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
type DischargeSlipDocumentProps = {
  file: MaternityFile;
  onClose: () => void;
};
export function DischargeSlipDocument({
  file,
  onClose
}: DischargeSlipDocumentProps) {
  const printableRef = useRef<HTMLElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const slip = file.dischargeSlip;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    setPdfError('');
    try {
      await captureToA4Pdf(printableRef.current, `BMED_DischargeSlip_${sanitizeFilename(file.bmcFileNumber)}_${sanitizeFilename(file.patient?.surname || '')}.pdf`);
    } catch (error) {
      console.error('Discharge slip PDF generation failed', error);
      setPdfError('The discharge slip PDF could not be generated. Please try again or use Print.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="discharge-slip-document-title" data-id="element-3241" data-mp="a4ave65n3w6">
      <div className="test-modal-print max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-3242" data-mp="2kxz5yg7bp3md">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-5 py-3 print:hidden" data-id="element-3243" data-mp="2rdpu6k4b4u5m">
          <div data-id="element-3244" data-mp="2hguwu4jrszsx">
            <h2 id="discharge-slip-document-title" className="text-lg font-bold text-gray-900" data-id="element-3245" data-mp="2hgbcyiak81c9">
              Discharge slip preview
            </h2>
            <p className="text-sm text-gray-500" data-id="element-3246" data-mp="28hzoalt97dmg">
              {file.patient?.firstName} {file.patient?.surname} ·{' '}
              {file.bmcFileNumber}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-id="element-3247" data-mp="1yqd96edsouef">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-3248" data-mp="1yosfss37vhi5">
              <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-3249" data-mp="1ks5ekd48e56f" /> Print
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} isLoading={isGeneratingPdf} className="bg-pink-600 hover:bg-pink-700 focus:ring-pink-500" data-id="element-3250" data-mp="1pp5b9xf6xvkv">
              {!isGeneratingPdf && <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-3251" data-mp="1f5lbjqdmx5zf" />}
              {isGeneratingPdf ? 'Generating PDF…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500" aria-label="Close discharge slip preview" data-id="element-3252" data-mp="1b5uh9cowg7q5">
              <XIcon className="h-5 w-5" data-id="element-3253" data-mp="1yssu80dt8xjb" />
            </button>
          </div>
        </header>

        {pdfError && <p role="alert" className="mx-5 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 print:hidden" data-id="element-3254" data-mp="1twc450n1w93l">
            {pdfError}
          </p>}

        <article ref={printableRef} className="bg-white p-8 text-gray-900 sm:p-10 print:p-0" data-id="element-3255" data-mp="15rmtrz5hxxs">
          <header className="flex items-center gap-4 border-b-2 border-black pb-4" data-id="element-3256" data-mp="64bem90fe3y2">
            <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-16 w-16 shrink-0 object-contain" data-id="element-3257" data-mp="avteetqppyhg" />
            <div className="flex-1" data-id="element-3258" data-mp="3edbj4sb6updn">
              <p className="text-xl font-bold uppercase tracking-wide text-pink-600" data-id="element-3259" data-mp="3eecxp6rexu04">
                BMED Medical Centre
              </p>
              <p className="mt-0.5 text-sm italic text-gray-600" data-id="element-3260" data-mp="3ncggvwexeeor">
                Caring Healing Leading
              </p>
            </div>
            <div className="text-right text-sm" data-id="element-3261" data-mp="3sdmbjzs5cc0x">
              <p data-id="element-3262" data-mp="12mzfbce9c07s">
                <span className="font-semibold" data-id="element-3263" data-mp="12nini1e3va8m">File No:</span>{' '}
                {file.bmcFileNumber}
              </p>
              <p data-id="element-3264" data-mp="17k93kopwu5u1">
                <span className="font-semibold" data-id="element-3265" data-mp="17nwynsb070av">SM No:</span>{' '}
                {slip?.smNo || '—'}
              </p>
            </div>
          </header>

          <h1 className="my-7 text-center text-lg font-bold uppercase tracking-[0.12em] underline" data-id="element-3266" data-mp="1cqe97696naf3">
            Maternity Discharge Slip
          </h1>

          <section className="grid gap-x-8 gap-y-2 border border-black p-4 text-sm sm:grid-cols-2" aria-label="Patient details" data-id="element-3267" data-mp="17xl3og5w3y1y">
            <Field label="Patient's name" value={slip?.patientName || `${file.patient?.firstName || ''} ${file.patient?.surname || ''}`.trim()} data-id="element-3268" data-mp="z02dyyhawvjq" />
            <Field label="Age" value={slip?.age || file.patient?.age} data-id="element-3269" data-mp="p2wotvs8zem5" />
            <Field label="Gravida" value={slip?.gravida} data-id="element-3270" data-mp="l12rnxh9gsqq" />
            <Field label="Party" value={slip?.party} data-id="element-3271" data-mp="2jfmh692ywfrf" />
            <Field label="Date of birth" value={slip?.motherDob} data-id="element-3272" data-mp="2jd0byg3juccd" />
            <Field label="Mode of delivery" value={slip?.modeOfDelivery} data-id="element-3273" data-mp="2feke1h5xjjk6" />
            <Field label="Date of discharge" value={slip?.dateOfDischarge} data-id="element-3274" data-mp="cqc35lw5u6xs" />
            <Field label="Time of discharge" value={slip?.timeOfDischarge} data-id="element-3275" data-mp="hhkdql987ycj" />
          </section>

          <section className="mt-6" aria-labelledby="discharge-mother-title" data-id="element-3276" data-mp="3q5v4fj8dz60">
            <h2 id="discharge-mother-title" className="border border-b-0 border-black bg-gray-100 px-3 py-2 text-sm font-bold uppercase tracking-wide" data-id="element-3277" data-mp="3o2uz2od6pnk">
              Mother on discharge
            </h2>
            <div className="grid gap-x-8 gap-y-2 border border-black p-4 text-sm sm:grid-cols-2" data-id="element-3278" data-mp="10gyb7d65zr7s">
              <Field label="B/P" value={slip?.mother?.bp} data-id="element-3279" data-mp="mh6tp2gfvsp1" />
              <Field label="Pallor" value={slip?.mother?.pallor} data-id="element-3280" data-mp="mirlwlfa6zjq" />
              <Field label="Breast" value={slip?.mother?.breast} data-id="element-3281" data-mp="refaaylxvoo6" />
              <Field label="Breastfeeding" value={slip?.mother?.breastfeeding} data-id="element-3282" data-mp="376czko9h00fu" />
              <Field label="Uterus" value={slip?.mother?.uterus} data-id="element-3283" data-mp="3c6qwba5mfnyk" />
              <Field label="Perineum" value={slip?.mother?.perineum} data-id="element-3284" data-mp="33op0c8m461xa" />
              <Field label="Lochia" value={slip?.mother?.lochia} data-id="element-3285" data-mp="33qtb9aifk172" />
              <Field label="Bowels" value={slip?.mother?.bowels} data-id="element-3286" data-mp="2zmlmxefvwn9d" />
              <Field label="Urine" value={slip?.mother?.urine} data-id="element-3287" data-mp="3dno7tnv57rip" />
              <Field label="Family planning" value={slip?.mother?.familyPlanning} data-id="element-3288" data-mp="3dksb22v178vu" />
              <Field label="Vitamin A" value={slip?.mother?.vitaminA} data-id="element-3289" data-mp="2kxxqz724mbiw" />
              <Field label="Danger signs" value={slip?.mother?.dangerSigns} data-id="element-3290" data-mp="2kw4h7iw7vb6o" />
              <div className="sm:col-span-2" data-id="element-3291" data-mp="2g17baku8hhy0">
                <Field label="Treatment" value={slip?.mother?.treatment} data-id="element-3292" data-mp="2uosfki2uhn7i" />
              </div>
              <div className="sm:col-span-2" data-id="element-3293" data-mp="2pxttt2z37wwv">
                <Field label="Comments" value={slip?.comments} data-id="element-3294" data-mp="2pvzcrzqxsoy2" />
              </div>
            </div>
          </section>

          <section className="mt-6" aria-labelledby="discharge-infant-title" data-id="element-3295" data-mp="225wo7tm8e147">
            <h2 id="discharge-infant-title" className="border border-b-0 border-black bg-gray-100 px-3 py-2 text-sm font-bold uppercase tracking-wide" data-id="element-3296" data-mp="1x8d4wfaoey5e">
              Infant on discharge
            </h2>
            <div className="grid gap-x-8 gap-y-2 border border-black p-4 text-sm sm:grid-cols-2" data-id="element-3297" data-mp="1lo0y63clwg3m">
              <Field label="Sex" value={slip?.infantSex} data-id="element-3298" data-mp="1qmkmgaeoa3z5" />
              <Field label="Time of birth" value={slip?.infantTob} data-id="element-3299" data-mp="1qo5ftwp93gvf" />
              <Field label="Apgar score" value={slip?.apgarScore} data-id="element-3300" data-mp="1unead3s47is1" />
              <Field label="P.C.A" value={slip?.pca} data-id="element-3301" data-mp="1zn8zb1sbxyag" />
              <Field label="Birth weight" value={slip?.infant?.birthWeight} data-id="element-3302" data-mp="1zlxtkwfbqggp" />
              <Field label="Discharge weight" value={slip?.infant?.dischargeWeight} data-id="element-3303" data-mp="24glbudjqiemu" />
              <Field label="Pallor" value={slip?.infant?.pallor} data-id="element-3304" data-mp="2ecydpypfnyso" />
              <Field label="Jaundice" value={slip?.infant?.jaundice} data-id="element-3305" data-mp="2ealpx4wzuu7p" />
              <Field label="Passed meconium / urine" value={slip?.infant?.passedMeconiumUrine} data-id="element-3306" data-mp="2iejlx6r0vowj" />
              <Field label="Feeding" value={slip?.infant?.feeding} data-id="element-3307" data-mp="2pbeb9j24lof" />
              <Field label="Feeding option" value={slip?.infant?.feedingOption} data-id="element-3308" data-mp="2r60ss9z8lum" />
              <Field label="Breastfeeding" value={slip?.infant?.breastFeeding} data-id="element-3309" data-mp="3ow9qkfdmy5aj" />
              <Field label="BCG" value={slip?.infant?.bcg} data-id="element-3310" data-mp="qexdhwzfj0wf" />
              <Field label="OPV" value={slip?.infant?.opv} data-id="element-3311" data-mp="qggwvwgdts2f" />
              <Field label="Birth record" value={slip?.infant?.birthRecord} data-id="element-3312" data-mp="lg2w8zh41kef" />
              <Field label="G/C" value={slip?.infant?.gc} data-id="element-3313" data-mp="gnsxqv1xjc0j" />
              <div className="sm:col-span-2" data-id="element-3314" data-mp="bprfudg8piqn">
                <Field label="Treatment" value={slip?.infant?.treatment} data-id="element-3315" data-mp="boq0vxw3sc3n" />
              </div>
            </div>
          </section>

          <section className="mt-6 grid gap-x-8 gap-y-2 border border-black p-4 text-sm sm:grid-cols-2" aria-label="Postnatal appointments" data-id="element-3316" data-mp="1978aja7ek4mm">
            <Field label="6 days postnatal date" value={slip?.sixDaysPostnatalDate} data-id="element-3317" data-mp="1bcqvj6vefm1t" />
            <Field label="6 weeks postnatal date" value={slip?.sixWeeksPostnatalDate} data-id="element-3318" data-mp="16ez3mcur7zxm" />
          </section>

          <footer className="mt-14 flex justify-end" data-id="element-3319" data-mp="1gasku9jf4hlq">
            <div className="w-72" data-id="element-3320" data-mp="1gdet6be4v150">
              <div className="border-b border-black pb-1 text-sm font-medium" data-id="element-3321" data-mp="1uabl6g5zmonh">
                {slip?.nameInFull || '\u00A0'}
              </div>
              <p className="mt-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-700" data-id="element-3322" data-mp="1pbgqafuqx9n0">
                Discharging midwife / clinician
              </p>
            </div>
          </footer>
        </article>
      </div>
    </div>;
}
function Field({
  label,
  value
}: {
  label: string;
  value?: string;
}) {
  return <p className="flex items-baseline gap-2" data-id="element-3323" data-mp="28zpph9upifw0">
      <span className="font-semibold" data-id="element-3324" data-mp="2910tnas2dpnn">{label}:</span>
      <span className="min-w-0 flex-1 border-b border-dotted border-gray-400" data-id="element-3325" data-mp="2ro9pwf2z94fn">
        {value || '\u00A0'}
      </span>
    </p>;
}
function sanitizeFilename(value: string) {
  return value.replace(/[^a-z0-9-_]/gi, '_') || 'maternity';
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}